// Banded Report Designer — FastReport benzeri.
// 8 band: ReportHeader / PageHeader / GroupHeader / Master / Detail / GroupFooter / PageFooter / ReportFooter
// Element tipleri: text, field, expression, image, line, rect, barcode
// Element özellikleri: x/y/w/h (mm), rotation, font, renk, hizalama, koşullu format
// Master-detail data source, gruplama (group by + sort), expression evaluator (Sum/Count/Avg/Min/Max + IIF + aritmetik)
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Rnd } from 'react-rnd';
import api from '../../services/api';
import { Button } from '../../components/ui';
import {
  PAPER_SIZES, BANDS, BAND_LABELS, BAND_COLORS, FONT_OPTIONS,
  createEmptyLayout, renderPrintHtml, evaluateExpr,
} from './reportEngine';
import {
  FIELD_REGISTRY, REPORT_DATA_SOURCES,
  SELECTABLE_MASTER_SOURCES, SELECTABLE_DETAIL_SOURCES,
  resolveSourceTypeForKey, getFieldsFor,
} from './fieldRegistry';

const DEFAULT_SCALE = 3.5;

// Örnek master verisi (designer'da kalan band'lar bunu gösterir)
const SAMPLE_MAIN = [
  { id: '1', code: 'STK001', name: 'Örnek Ürün A', barcode: '8690000000017', category: 'Genel',     brand: 'Marka X', unit: 'Adet', vat: 20, total_qty: 35 },
  { id: '2', code: 'STK002', name: 'Örnek Ürün B', barcode: '8690000000024', category: 'Hırdavat',  brand: 'Marka Y', unit: 'Kg',   vat: 10, total_qty: 40 },
  { id: '3', code: 'STK003', name: 'Örnek Ürün C', barcode: '8690000000031', category: 'Genel',     brand: 'Marka Z', unit: 'Mt',   vat: 1,  total_qty: 20 },
];
const SAMPLE_DETAIL_BY_MASTER = {
  '1': [{ master_id: '1', date: '2026-05-01', warehouse: 'Ana Depo', qty: 25 }, { master_id: '1', date: '2026-05-03', warehouse: 'Şube', qty: 10 }],
  '2': [{ master_id: '2', date: '2026-05-02', warehouse: 'Şube', qty: 40 }],
  '3': [{ master_id: '3', date: '2026-05-04', warehouse: 'Ana Depo', qty: 12 }, { master_id: '3', date: '2026-05-05', warehouse: 'Ana Depo', qty: 8 }],
};

// ─── ROOT ────────────────────────────────────────────────────────
export default function ReportDesignerPage({
  onNavigate,
  // Belge tasarımı için override edilebilir prop'lar (varsayılan: stok listesi raporu):
  reportKey: reportKeyProp,                        // örn: 'sales_quote_doc'
  apiBase = '/reports/templates',                  // belge için '/print-templates'
  apiListParam = 'report_key',                      // belge için 'doc_type'
  initialTplId = null,                              // mevcut şablonu yüklemek için
  onClose,                                          // overlay'den kapatmak için
  pageTitle,                                        // başlık özelleştirme
  previewData = null,                               // belge modunda gerçek veri: { mainRow, detailRows, context }
}) {
  const [tplId, setTplId]       = useState(initialTplId);
  const [tplName, setTplName]   = useState(initialTplId ? '' : 'Yeni Şablon');
  const reportKey               = reportKeyProp || 'stock_list';
  const [layout, _setLayout]    = useState(createEmptyLayout(reportKey));
  const [selectedId, setSelected] = useState(null);
  const [scale, setScale]       = useState(DEFAULT_SCALE);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [activePanel, setActivePanel] = useState('layout'); // layout | data | bands | group
  const [snapStep, setSnapStep] = useState(1);    // mm
  const [showGuides, setShowGuides] = useState(true);
  const [activeGuides, setActiveGuides] = useState({ x: null, y: null }); // sürükleme sırasında görünen mavi çizgi (mm)

  // ─── UNDO/REDO history ──────────────────────────────────────────
  const historyRef = useRef({ past: [], future: [] });
  const skipHistoryRef = useRef(false);

  const setLayout = useCallback((next) => {
    _setLayout(prev => {
      const newLayout = (typeof next === 'function') ? next(prev) : next;
      if (!skipHistoryRef.current) {
        // Önceki state'i past'a push et
        historyRef.current.past.push(prev);
        if (historyRef.current.past.length > 50) historyRef.current.past.shift();
        historyRef.current.future = []; // yeni eylemde redo zincirini temizle
      }
      skipHistoryRef.current = false;
      return newLayout;
    });
  }, []);

  const undo = useCallback(() => {
    const prev = historyRef.current.past.pop();
    if (!prev) return;
    historyRef.current.future.push(layout);
    skipHistoryRef.current = true;
    _setLayout(prev);
  }, [layout]);

  const redo = useCallback(() => {
    const next = historyRef.current.future.pop();
    if (!next) return;
    historyRef.current.past.push(layout);
    skipHistoryRef.current = true;
    _setLayout(next);
  }, [layout]);

  const isDocMode = apiBase !== '/reports/templates';
  // Şablon yükle (rapor: localStorage'dan ID; belge: initialTplId prop'undan)
  useEffect(() => {
    const id = isDocMode ? initialTplId : (() => {
      const v = localStorage.getItem('report_designer_id');
      try { localStorage.removeItem('report_designer_id'); } catch (_) {}
      return v;
    })();
    if (id && id !== 'new') {
      setLoading(true);
      const params = isDocMode ? { doc_type: reportKey.replace(/_doc$/, '') } : { report_key: reportKey };
      api.get(apiBase, { params })
        .then(async (r) => {
          if (!r.data.success) return;
          const t = (r.data.data || []).find(x => x.id === id);
          if (!t) return;
          setTplId(t.id); setTplName(t.name);
          let cfg = isDocMode ? t.layout_json : t.config_json;
          if (typeof cfg === 'string') { try { cfg = JSON.parse(cfg); } catch { cfg = {}; } }
          let lay = isDocMode ? cfg : cfg?.layout;
          // Belge modunda layout yoksa varsayılan layout yükle (eski/boş şablonlar için)
          if (isDocMode && (!lay || !Array.isArray(lay.elements) || lay.elements.length === 0)) {
            try {
              const mod = await import('../printTemplates/defaults');
              const docType = reportKey.replace(/_doc$/, '');
              lay = mod.getDefaultLayout(docType);
            } catch (_) {}
          }
          if (lay) setLayout({ ...createEmptyLayout(reportKey), ...lay, bands: { ...createEmptyLayout(reportKey).bands, ...(lay.bands || {}) } });
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line
  }, []);

  // ─── State helpers ────────────────────────────────────────────
  const update         = (patch) => setLayout(prev => ({ ...prev, ...patch }));
  const updateBand     = (band, patch) => setLayout(prev => ({ ...prev, bands: { ...prev.bands, [band]: { ...prev.bands[band], ...patch } } }));
  const updateElement  = (id, patch) => setLayout(prev => ({ ...prev, elements: prev.elements.map(el => el.id === id ? { ...el, ...patch } : el) }));
  const deleteElement  = (id) => {
    setLayout(prev => ({ ...prev, elements: prev.elements.filter(el => el.id !== id) }));
    if (selectedId === id) setSelected(null);
  };
  const duplicateElement = (id) => {
    const el = layout.elements.find(e => e.id === id);
    if (!el) return;
    const newEl = { ...el, id: 'el_' + Date.now() + '_' + Math.random().toString(36).slice(2, 5), x: (el.x || 0) + 3, y: (el.y || 0) + 3 };
    setLayout(prev => ({ ...prev, elements: [...prev.elements, newEl] }));
    setSelected(newEl.id);
  };
  // Z-order — array sırasına göre render edilir (sonraki üstte). Sıralama band içinde anlamlı değil; band karışıklığı olmasın diye band gözetilir.
  const moveZ = (id, direction) => {
    setLayout(prev => {
      const arr = [...prev.elements];
      const idx = arr.findIndex(e => e.id === id);
      if (idx < 0) return prev;
      const el = arr[idx];
      // En öne / en arkaya getir
      if (direction === 'front')  { arr.splice(idx, 1); arr.push(el);    return { ...prev, elements: arr }; }
      if (direction === 'back')   { arr.splice(idx, 1); arr.unshift(el); return { ...prev, elements: arr }; }
      // Bir adım öne / bir adım arkaya — band içinde komşu element ile yer değiştir
      const step = direction === 'forward' ? 1 : -1;
      let swapIdx = idx + step;
      // Aynı band'da değil ise atla
      while (swapIdx >= 0 && swapIdx < arr.length && arr[swapIdx].band !== el.band) swapIdx += step;
      if (swapIdx < 0 || swapIdx >= arr.length) return prev;
      [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
      return { ...prev, elements: arr };
    });
  };
  const addElement = (band, type) => {
    const id = 'el_' + Date.now() + '_' + Math.random().toString(36).slice(2, 5);
    const base = { id, band, x: 5, y: 1, w: 50, h: 6, rotation: 0, align: 'left', valign: 'middle' };
    let el;
    if (type === 'text')       el = { ...base, type, text: 'Yazı', font_size: 11, bold: false, color: '#000' };
    if (type === 'field')      el = { ...base, type, data_source: band === 'detail' ? 'detail' : 'main', field: 'name', font_size: 10, color: '#000' };
    if (type === 'expression') el = { ...base, type, data_source: 'main', expression: 'Sum([total_qty])', font_size: 11, bold: true, color: '#000', format: '' };
    if (type === 'image')      el = { ...base, type, src: '', w: 30, h: 20, fit: 'contain' };
    if (type === 'line')       el = { ...base, type, h: 0.4, color: '#000', thickness: 1, w: 100 };
    if (type === 'rect')       el = { ...base, type, w: 60, h: 12, border_color: '#3b82f6', border_width: 1, background: 'transparent' };
    if (type === 'barcode')    el = { ...base, type, data_source: 'main', field: 'barcode', font_size: 22, w: 50, h: 14 };
    setLayout(prev => ({ ...prev, elements: [...prev.elements, el] }));
    setSelected(id);
  };

  // Klavye kısayolları (Delete, Ctrl+D, oklar, Ctrl+Z, Ctrl+Y)
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && e.key.toLowerCase() === 'z' && !e.shiftKey) { undo(); e.preventDefault(); return; }
      if (isMod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) { redo(); e.preventDefault(); return; }
      if (!selectedId) return;
      if (e.key === 'Delete') { deleteElement(selectedId); e.preventDefault(); }
      else if (e.key.toLowerCase() === 'd' && isMod) { duplicateElement(selectedId); e.preventDefault(); }
      else if (e.key.startsWith('Arrow')) {
        const el = layout.elements.find(x => x.id === selectedId);
        if (!el) return;
        const step = e.shiftKey ? 5 : 1;
        const patch = {};
        if (e.key === 'ArrowLeft')  patch.x = Math.max(0, (el.x || 0) - step);
        if (e.key === 'ArrowRight') patch.x = (el.x || 0) + step;
        if (e.key === 'ArrowUp')    patch.y = Math.max(0, (el.y || 0) - step);
        if (e.key === 'ArrowDown')  patch.y = (el.y || 0) + step;
        if (Object.keys(patch).length) { updateElement(selectedId, patch); e.preventDefault(); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedId, layout.elements, undo, redo]); // eslint-disable-line

  const selected = useMemo(() => layout.elements.find(e => e.id === selectedId) || null, [layout.elements, selectedId]);

  // Save
  const handleSave = async () => {
    if (!tplName.trim()) return alert('Şablon adı gerekli');
    setSaving(true);
    try {
      if (isDocMode) {
        // Belge şablonu — print_templates tablosuna kaydet (layout_json olarak)
        const docType = reportKey.replace(/_doc$/, '');
        const body = {
          doc_type: docType, name: tplName, is_default: false,
          paper_size: layout.paper_size || 'A4',
          orientation: layout.page_orientation || 'portrait',
          font_size: 10, margin_mm: 15,
          layout_json: layout,
        };
        if (tplId) await api.put(apiBase + '/' + tplId, body);
        else {
          const r = await api.post(apiBase, body);
          if (r.data.success) setTplId(r.data.data?.id || r.data.id);
        }
      } else {
        const config = { layout, title: tplName, paper_size: layout.paper_size, page_orientation: layout.page_orientation };
        if (tplId) await api.put(apiBase + '/' + tplId, { name: tplName, is_default: false, config });
        else {
          const r = await api.post(apiBase, { report_key: reportKey, name: tplName, is_default: false, config });
          if (r.data.success) setTplId(r.data.id);
        }
      }
      alert('Şablon kaydedildi');
      if (onClose) onClose(true);
    } catch (e) { alert(e.response?.data?.message || 'Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  // Önizleme: örnek master+detail data ile yazdırma
  // Belge modunda (doc_header/doc_line) farklı örnek veri seti
  const handlePreview = useCallback(() => {
    const w = window.open('', '_blank');
    if (!w) return alert('Pop-up engellendi');
    let mainRows = SAMPLE_MAIN, detailMap = SAMPLE_DETAIL_BY_MASTER, ctx;
    // Belge modunda gerçek veri varsa onu kullan, yoksa örnek veri
    if (isDocMode && previewData?.mainRow) {
      const m = previewData.mainRow;
      const dRows = previewData.detailRows || [];
      mainRows = [m];
      detailMap = { [m.id || 'preview-doc']: dRows };
      ctx = previewData.context || {
        title: tplName || 'Önizleme',
        subtitle: m.document_number || '',
        date_today: new Date().toISOString().slice(0, 10),
        row_count: dRows.length,
      };
      const html = renderPrintHtml({ layout, name: tplName, mainRows, detailRowsByMaster: detailMap, context: ctx });
      w.document.write(html); w.document.close();
      return;
    }
    if (isDocMode) {
      const sampleDoc = {
        id: 'preview-doc',
        document_number: 'VT2026000000999',
        invoice_date: new Date().toISOString().slice(0, 10),
        invoice_time: '14:30',
        due_date: '',
        valid_until: '2026-06-30',
        account_code: 'CRI001',
        account_title: 'Örnek Müşteri A.Ş.',
        account_tax_office: 'Bursa VD',
        account_tax_number: '1234567890',
        account_address: 'Atatürk Cd. No:12',
        account_city: 'Bursa',
        account_district: 'Nilüfer',
        account_country: 'Türkiye',
        account_phone: '0224 555 00 00',
        account_mobile: '0555 555 00 00',
        account_email: 'info@ornekmusteri.com',
        personnel_name: 'Ahmet Yılmaz',
        currency_code: 'TRY',
        exchange_rate: 1,
        subtotal: 10000,
        discount_total: 500,
        net_total: 9500,
        vat_total: 1900,
        grand_total: 11400,
        avg_profit_pct: 22.5,
        description: 'Örnek belge — tasarım önizlemesi',
        notes: 'Bu çıktı önizleme amaçlıdır. Gerçek belgelerde veri otomatik dolar.',
        internal_notes: '1-ARES-BIOCLIMATIK · Kar %: 21 · Euro / TL: 52 · Dolar / TL: 45,5\n2-HELEN-PERGOLA 3 RAYLI · Cephe: 4 · Açılım: 6,155',
      };
      const sampleLines = [
        { master_id: 'preview-doc', line_no: 1, stock_code: 'STK001', stock_name: 'Pergola Bioclimatic 3x4', description: '', quantity: 1,  unit_code: 'Adet', unit_price: 5000,  vat_rate: 20, line_total: 6000 },
        { master_id: 'preview-doc', line_no: 2, stock_code: 'STK002', stock_name: 'Yan Cam Sistemi',         description: 'Şeffaf',  quantity: 2,  unit_code: 'm²',   unit_price: 1500,  vat_rate: 20, line_total: 3600 },
        { master_id: 'preview-doc', line_no: 3, stock_code: 'STK003', stock_name: 'LED Aydınlatma',           description: 'RGB',     quantity: 4,  unit_code: 'Adet', unit_price: 450,   vat_rate: 20, line_total: 2160 },
      ];
      mainRows = [sampleDoc];
      detailMap = { 'preview-doc': sampleLines };
      ctx = { title: tplName || 'Önizleme', subtitle: sampleDoc.document_number,
              company_name: 'Firma A.Ş.', footer_text: 'Önizleme — örnek veri',
              date_today: new Date().toISOString().slice(0, 10),
              row_count: sampleLines.length };
    } else {
      ctx = { title: tplName, subtitle: 'Önizleme', company_name: 'Firma A.Ş.',
              filter_summary: 'Sadece aktif · Tüm depolar', footer_text: 'Önizleme — örnek veri' };
    }
    const html = renderPrintHtml({ layout, name: tplName, mainRows, detailRowsByMaster: detailMap, context: ctx });
    w.document.write(html); w.document.close();
  }, [layout, tplName, isDocMode]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div>;

  return (
    <div className="stocks-page" style={{ display: 'flex', flexDirection: 'column', height: isDocMode ? '100vh' : 'calc(100vh - 56px)' }}>
      {/* Toolbar */}
      <div style={{ padding: 8, borderBottom: '1px solid var(--border-secondary)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <Button variant="ghost" size="sm" onClick={() => {
          if (isDocMode && onClose) onClose(false);
          else if (onNavigate) onNavigate('reports-stock-list');
        }}>← {isDocMode ? 'Kapat' : 'Geri'}</Button>
        {pageTitle && <strong style={{ fontSize: '0.95rem', marginRight: 8 }}>{pageTitle}</strong>}
        <input value={tplName} onChange={e => setTplName(e.target.value)} placeholder="Şablon adı"
          style={{ flex: '1 1 200px', minWidth: 0, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', fontWeight: 600 }}/>
        <Button size="sm" variant="ghost" onClick={undo} disabled={historyRef.current.past.length === 0} title="Geri al (Ctrl+Z)">↶</Button>
        <Button size="sm" variant="ghost" onClick={redo} disabled={historyRef.current.future.length === 0} title="Yinele (Ctrl+Y)">↷</Button>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', borderLeft: '1px solid var(--border-secondary)', paddingLeft: 8 }}>Snap</span>
        <select value={snapStep} onChange={e => setSnapStep(parseFloat(e.target.value))}
          style={{ padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', fontSize: 11 }} title="Sürükleme adımı">
          <option value="0">Yok</option>
          <option value="0.5">0.5mm</option>
          <option value="1">1mm</option>
          <option value="2">2mm</option>
          <option value="5">5mm</option>
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer' }} title="Hizalama kılavuzları">
          <input type="checkbox" checked={showGuides} onChange={e => setShowGuides(e.target.checked)}/> Kılavuz
        </label>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', borderLeft: '1px solid var(--border-secondary)', paddingLeft: 8 }}>Yakınlık</span>
        <input type="range" min={1.5} max={6} step={0.25} value={scale} onChange={e => setScale(parseFloat(e.target.value))} style={{ width: 110 }}/>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', minWidth: 40 }}>×{scale.toFixed(2)}</span>
        <Button size="sm" variant="ghost" onClick={handlePreview}>🖨 Önizle</Button>
        <Button size="sm" variant="ghost" onClick={() => {
          // Şablonu JSON dosyası olarak indir
          const exportData = {
            name: tplName || 'Şablon',
            report_key: reportKey,
            exported_at: new Date().toISOString(),
            layout,
          };
          const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${(tplName || 'sablon').replace(/[^a-z0-9_\-]/gi, '_')}.json`;
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }} title="Şablonu JSON dosyası olarak indir">⬇ Dışa Aktar</Button>
        <Button size="sm" variant="ghost" onClick={() => {
          // Dosya seç → şablonu yükle
          const input = document.createElement('input');
          input.type = 'file'; input.accept = '.json,application/json';
          input.onchange = (e) => {
            const file = e.target.files?.[0]; if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => {
              try {
                const data = JSON.parse(ev.target.result);
                const lay = data.layout || data;
                if (!lay || !Array.isArray(lay.elements)) {
                  alert('Geçersiz şablon dosyası — "layout" + "elements" alanları gerekli');
                  return;
                }
                if (data.report_key && data.report_key !== reportKey) {
                  if (!window.confirm(`Bu şablon "${data.report_key}" için tasarlanmış (mevcut: ${reportKey}). Yine de yüklensin mi?`)) return;
                }
                if (!window.confirm('Mevcut tasarımın üzerine yazılacak. Devam edilsin mi?')) return;
                setLayout({ ...createEmptyLayout(reportKey), ...lay, bands: { ...createEmptyLayout(reportKey).bands, ...(lay.bands || {}) } });
                if (data.name && !tplName) setTplName(data.name);
              } catch (err) { alert('Dosya okunamadı: ' + err.message); }
            };
            reader.readAsText(file);
          };
          input.click();
        }} title="Şablon JSON dosyası yükle (mevcut tasarımın üzerine yazılır)">⬆ İçe Aktar</Button>
        <Button size="sm" onClick={handleSave} loading={saving}>💾 Kaydet</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr 320px', flex: 1, overflow: 'hidden' }}>
        {/* SOL: tablar (Layout / Data / Bands / Group) + Element ekle + Element listesi */}
        <div style={{ borderRight: '1px solid var(--border-secondary)', overflowY: 'auto', background: 'var(--bg-card)' }}>
          <Tabs active={activePanel} onChange={setActivePanel}/>
          <div style={{ padding: 12 }}>
            {activePanel === 'layout' && <LayoutPanel layout={layout} update={update}/>}
            {activePanel === 'data'   && <DataPanel   layout={layout} update={update} reportKey={reportKey}/>}
            {activePanel === 'bands'  && <BandsPanel  layout={layout} updateBand={updateBand} onAddElement={addElement}/>}
            {activePanel === 'group'  && <GroupPanel  layout={layout} update={update}/>}
            <hr style={{ margin: '14px 0', border: 0, borderTop: '1px solid var(--border-secondary)' }}/>
            <ElementsTreeList elements={layout.elements} selectedId={selectedId} onSelect={setSelected} onDelete={deleteElement}/>
          </div>
        </div>

        {/* ORTA: Tuval */}
        <div style={{ overflow: 'auto', padding: 16, background: '#e2e8f0', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
          <DesignerCanvas
            layout={layout} scale={scale}
            selectedId={selectedId} onSelect={setSelected}
            onUpdateElement={updateElement}
            onUpdateBand={updateBand}
            snapStep={snapStep}
            showGuides={showGuides}
            activeGuides={activeGuides}
            setActiveGuides={setActiveGuides}
            onDropField={(payload, dropX, dropY) => {
              // Sol panelden alan sürüklenince yeni field element oluştur
              const { source, fieldKey, band } = payload;
              const id = 'el_' + Date.now() + '_' + Math.random().toString(36).slice(2, 5);
              const el = { id, band, type: 'field', data_source: source, field: fieldKey,
                x: Math.max(0, dropX), y: Math.max(0, dropY),
                w: 40, h: 6, rotation: 0, font_size: 10, color: '#000', align: 'left', valign: 'middle' };
              setLayout(prev => ({ ...prev, elements: [...prev.elements, el] }));
              setSelected(id);
            }}
          />
        </div>

        {/* SAĞ: Element özellikleri */}
        <div style={{ borderLeft: '1px solid var(--border-secondary)', overflowY: 'auto', padding: 12, background: 'var(--bg-card)' }}>
          {!selected
            ? <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                Bir element seçin veya soldan band'a ekleyin.<br/><br/>
                <strong>Klavye:</strong><br/>
                · Del: sil · Ctrl+D: kopyala<br/>
                · Ctrl+Z: geri al · Ctrl+Y: yinele<br/>
                · Oklar: 1mm · Shift+Ok: 5mm<br/><br/>
                <strong>Sürükle-bırak:</strong><br/>
                Veri sekmesinde alanın yanındaki ⋮⋮ ikonunu tutarak tuvale sürükle, otomatik field oluşur.
              </div>
            : <ElementPropsPanel el={selected} layout={layout} onChange={(p) => updateElement(selected.id, p)} onDelete={() => deleteElement(selected.id)} onDuplicate={() => duplicateElement(selected.id)} onMoveZ={(dir) => moveZ(selected.id, dir)}/>
          }
        </div>
      </div>
    </div>
  );
}

// ═══ TABS ════════════════════════════════════════════════════════
function Tabs({ active, onChange }) {
  const tabs = [
    { key: 'layout', label: 'Sayfa' },
    { key: 'data',   label: 'Veri' },
    { key: 'bands',  label: 'Bantlar' },
    { key: 'group',  label: 'Grup' },
  ];
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)' }}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)}
          style={{ flex: 1, padding: '8px 6px', border: 'none', cursor: 'pointer', fontSize: 12,
                   background: active === t.key ? 'var(--bg-card)' : 'transparent',
                   borderBottom: active === t.key ? '2px solid var(--accent-primary)' : '2px solid transparent',
                   color: active === t.key ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: active === t.key ? 700 : 500 }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ═══ LAYOUT (Sayfa) ══════════════════════════════════════════════
function LayoutPanel({ layout, update }) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <Field label="Kağıt Boyutu">
        <select value={layout.paper_size} onChange={e => update({ paper_size: e.target.value })} style={inputS}>
          {Object.keys(PAPER_SIZES).map(k => <option key={k} value={k}>{k}</option>)}
        </select>
      </Field>
      <Field label="Yön">
        <select value={layout.page_orientation} onChange={e => update({ page_orientation: e.target.value })} style={inputS}>
          <option value="portrait">Dikey</option><option value="landscape">Yatay</option>
        </select>
      </Field>
      <Row>
        <Field label="Üst (mm)"><input type="number" value={layout.margin_top}    onChange={e => update({ margin_top: parseFloat(e.target.value) || 0 })} style={inputS}/></Field>
        <Field label="Alt (mm)"><input type="number" value={layout.margin_bottom} onChange={e => update({ margin_bottom: parseFloat(e.target.value) || 0 })} style={inputS}/></Field>
      </Row>
      <Row>
        <Field label="Sol (mm)"><input type="number" value={layout.margin_left}  onChange={e => update({ margin_left: parseFloat(e.target.value) || 0 })} style={inputS}/></Field>
        <Field label="Sağ (mm)"><input type="number" value={layout.margin_right} onChange={e => update({ margin_right: parseFloat(e.target.value) || 0 })} style={inputS}/></Field>
      </Row>
      <Field label="Vurgu Rengi"><input type="color" value={layout.accent_color} onChange={e => update({ accent_color: e.target.value })} style={{ width: '100%', height: 28 }}/></Field>
      <Field label="Varsayılan Yazı Tipi">
        <select value={layout.font_family} onChange={e => update({ font_family: e.target.value })} style={inputS}>
          {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </Field>
      <Field label="Varsayılan Punto"><input type="number" min="6" max="36" value={layout.font_size} onChange={e => update({ font_size: parseFloat(e.target.value) || 10 })} style={inputS}/></Field>
    </div>
  );
}

// ═══ DATA (Veri Kaynakları) ══════════════════════════════════════
// Kullanıcı veri kümelerini SEÇER (registry'den) — alanları elle yazmaz.
// Alanlar otomatik olarak gelir; her alanın yanında ⋮⋮ tutamağı var, tuvale sürüklenir.
function DataPanel({ layout, update, reportKey }) {
  const ds = layout.data_sources || { main: { fields: [] }, detail: { fields: [] } };
  const setDS = (patch) => update({ data_sources: { ...ds, ...patch } });

  // Veri kümesinin tipini değiştir → ad ve alanları registry'den yenile
  const switchSourceType = (sourceKey, newType) => {
    if (!newType) {
      // detail için "kapat" seçeneği
      setDS({ [sourceKey]: { ...ds[sourceKey], source_type: null, name: '', fields: [] } });
      return;
    }
    const reg = FIELD_REGISTRY[newType];
    if (!reg) return;
    setDS({
      [sourceKey]: {
        ...ds[sourceKey],
        source_type: newType,
        name: reg.label,
        fields: reg.fields.slice(),
      },
    });
  };

  const mainType    = resolveSourceTypeForKey('main',    layout, reportKey);
  const detailType  = resolveSourceTypeForKey('detail',  layout, reportKey);
  const mainFields    = mainType    ? FIELD_REGISTRY[mainType]?.fields    || [] : (ds.main?.fields    || []);
  const detailFields  = detailType  ? FIELD_REGISTRY[detailType]?.fields  || [] : (ds.detail?.fields  || []);
  const contextFields = FIELD_REGISTRY.context?.fields || [];

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'rgba(59,130,246,0.08)', padding: '8px 10px', borderRadius: 6, lineHeight: 1.4 }}>
        💡 Alanın yanındaki <strong style={{ color: 'var(--accent-primary)' }}>⋮⋮</strong> tutamağını tutup tuvaldeki band'a sürükleyin — alan otomatik oluşur, anahtar yazmanıza gerek yok.
      </div>

      <DataSourceSection
        title="Ana Veri Kümesi"
        sourceKey="main"
        sourceType={mainType}
        fields={mainFields}
        selectableTypes={SELECTABLE_MASTER_SOURCES}
        onChangeType={(t) => switchSourceType('main', t)}
        nullable={false}
      />

      <DataSourceSection
        title="Detay Veri Kümesi (Master-Detail)"
        sourceKey="detail"
        sourceType={detailType}
        fields={detailFields}
        selectableTypes={SELECTABLE_DETAIL_SOURCES}
        onChangeType={(t) => switchSourceType('detail', t)}
        nullable={true}
        extraTop={detailType && (
          <Row>
            <Field label="Master Anahtar">
              <input type="text" value={ds.detail?.master_key || 'id'}
                onChange={e => setDS({ detail: { ...ds.detail, master_key: e.target.value } })} style={inputS}/>
            </Field>
            <Field label="Detay FK">
              <input type="text" value={ds.detail?.detail_key || 'master_id'}
                onChange={e => setDS({ detail: { ...ds.detail, detail_key: e.target.value } })} style={inputS}/>
            </Field>
          </Row>
        )}
      />

      <DataSourceSection
        title="Sayfa / Bağlam"
        sourceKey="context"
        sourceType="context"
        fields={contextFields}
        readOnlyType
      />
    </div>
  );
}

// Tek bir veri kümesi (master / detail / context) için tip seçici + alan ağacı
function DataSourceSection({ title, sourceKey, sourceType, fields, selectableTypes, onChangeType, nullable, readOnlyType, extraTop }) {
  const [open, setOpen] = useState(true);
  const reg = sourceType ? FIELD_REGISTRY[sourceType] : null;
  return (
    <div style={{ borderTop: '1px solid var(--border-secondary)', paddingTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <strong style={{ fontSize: 12 }}>{title}</strong>
        <button onClick={() => setOpen(s => !s)} style={collapseBtn}>{open ? '▾' : '▸'}</button>
      </div>

      {!readOnlyType && (
        <Field label="Tip">
          <select
            value={sourceType || ''}
            onChange={e => onChangeType(e.target.value || null)}
            style={inputS}>
            {nullable && <option value="">— kapalı —</option>}
            {selectableTypes.filter(Boolean).map(t => (
              <option key={t} value={t}>
                {FIELD_REGISTRY[t]?.icon || ''} {FIELD_REGISTRY[t]?.label || t}
              </option>
            ))}
          </select>
        </Field>
      )}

      {readOnlyType && reg && (
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>
          {reg.icon} {reg.label} <span style={{ opacity: 0.7 }}>(sabit)</span>
        </div>
      )}

      {extraTop}

      {open && sourceType && (
        <FieldsTree source={sourceKey} fields={fields}/>
      )}
      {open && !sourceType && !readOnlyType && (
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', padding: '8px 4px' }}>
          (kapalı — bir tip seçince alanlar görünür)
        </div>
      )}
    </div>
  );
}

// Veri kümesinin alanlarını liste hâlinde gösterir; her alan tuvale sürüklenebilir.
function FieldsTree({ fields, source }) {
  const onDragStart = (e, f) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ source, fieldKey: f.key, label: f.label }));
    e.dataTransfer.effectAllowed = 'copy';
  };
  if (!fields?.length) {
    return <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Alan yok.</div>;
  }
  return (
    <div style={{ display: 'grid', gap: 2, marginTop: 4 }}>
      {fields.map(f => (
        <div key={f.key}
          draggable
          onDragStart={e => onDragStart(e, f)}
          title={`Anahtar: ${f.key} — tuvale sürükleyin`}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 8px',
            background: 'var(--bg-secondary)',
            border: '1px solid transparent',
            borderRadius: 5,
            cursor: 'grab',
            fontSize: 12,
            userSelect: 'none',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}>
          <span style={{ color: 'var(--accent-primary)', fontSize: 13, lineHeight: 1 }}>⋮⋮</span>
          <span style={{ flex: 1, fontWeight: 500 }}>{f.label}</span>
          <span style={{ fontSize: 10, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
            {f.type === 'number' ? '#' : f.type === 'date' ? '📅' : 'A'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ═══ BANDS ═══════════════════════════════════════════════════════
function BandsPanel({ layout, updateBand, onAddElement }) {
  const [addingTo, setAddingTo] = useState('page_header');
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
        Görünür band'ları ve yüksekliklerini ayarlayın. Detail band master-detail için kullanılır.
      </div>
      {BANDS.map(b => {
        const cfg = layout.bands[b];
        return (
          <div key={b} style={{ padding: 8, background: BAND_COLORS[b], border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <input type="checkbox" checked={!!cfg.visible} onChange={e => updateBand(b, { visible: e.target.checked })}/>
              <strong style={{ flex: 1, fontSize: 12 }}>{BAND_LABELS[b]}</strong>
              <input type="number" step="0.5" value={cfg.height} onChange={e => updateBand(b, { height: parseFloat(e.target.value) || 0 })}
                style={{ ...inputS, width: 60 }} title="Yükseklik (mm)" disabled={!cfg.visible}/>
              <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>mm</span>
            </div>
          </div>
        );
      })}
      <hr style={{ border: 0, borderTop: '1px solid var(--border-secondary)' }}/>
      <Field label="Element Eklemek İçin Band Seç">
        <select value={addingTo} onChange={e => setAddingTo(e.target.value)} style={inputS}>
          {BANDS.filter(b => layout.bands[b]?.visible).map(b => <option key={b} value={b}>{BAND_LABELS[b]}</option>)}
        </select>
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
        <Button size="sm" variant="ghost" onClick={() => onAddElement(addingTo, 'text')}>📝 Yazı</Button>
        <Button size="sm" variant="ghost" onClick={() => onAddElement(addingTo, 'field')}>🏷 Alan</Button>
        <Button size="sm" variant="ghost" onClick={() => onAddElement(addingTo, 'expression')}>ƒ Formül</Button>
        <Button size="sm" variant="ghost" onClick={() => onAddElement(addingTo, 'image')}>🖼 Resim</Button>
        <Button size="sm" variant="ghost" onClick={() => onAddElement(addingTo, 'line')}>━ Çizgi</Button>
        <Button size="sm" variant="ghost" onClick={() => onAddElement(addingTo, 'rect')}>▭ Kutu</Button>
        <Button size="sm" variant="ghost" onClick={() => onAddElement(addingTo, 'barcode')}>📊 Barkod</Button>
      </div>
    </div>
  );
}

// ═══ GROUP ═══════════════════════════════════════════════════════
function GroupPanel({ layout, update }) {
  const fields = getFieldsFor('main', layout, 'stock_list');
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <Field label="Gruplama Alanı (Group By)">
        <select value={layout.group?.by_field || ''} onChange={e => update({ group: { ...layout.group, by_field: e.target.value } })} style={inputS}>
          <option value="">— gruplama yok —</option>
          {fields.map(f => <option key={f.key} value={f.key}>{f.label} ({f.key})</option>)}
        </select>
      </Field>
      <Field label="Sıralama">
        <select value={layout.group?.sort_dir || 'asc'} onChange={e => update({ group: { ...layout.group, sort_dir: e.target.value } })} style={inputS}>
          <option value="asc">A → Z</option><option value="desc">Z → A</option>
        </select>
      </Field>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
        Group seçilince Group Header / Group Footer band'larını "Bantlar" sekmesinden açın. <br/>
        Group Footer'da formül ile o grubun toplamını yazabilirsiniz: <code>Sum([total_qty])</code>
      </div>
    </div>
  );
}

// ═══ ELEMENT TREE LIST ══════════════════════════════════════════
function ElementsTreeList({ elements, selectedId, onSelect, onDelete }) {
  if (elements.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Henüz element yok.</div>;
  }
  const grouped = {};
  for (const el of elements) (grouped[el.band] = grouped[el.band] || []).push(el);
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 4 }}>Elementler</div>
      {BANDS.filter(b => grouped[b]).map(b => (
        <div key={b} style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', marginBottom: 2 }}>{BAND_LABELS[b]} ({grouped[b].length})</div>
          {grouped[b].map(el => (
            <div key={el.id} onClick={() => onSelect(el.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 6px', cursor: 'pointer', fontSize: 12, borderRadius: 4,
                       background: selectedId === el.id ? 'rgba(59,130,246,0.15)' : 'transparent' }}>
              <span style={{ width: 16 }}>{elementIcon(el.type)}</span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{elementSummary(el)}</span>
              <button onClick={(e) => { e.stopPropagation(); onDelete(el.id); }} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--danger)' }}>×</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
function elementIcon(t) { return ({ text: '📝', field: '🏷', expression: 'ƒ', image: '🖼', line: '━', rect: '▭', barcode: '📊' })[t] || '?'; }
function elementSummary(el) {
  if (el.type === 'text')       return (el.text || '').substring(0, 22) || 'Yazı';
  if (el.type === 'field')      return `${el.data_source || 'main'}.${el.field || ''}`;
  if (el.type === 'expression') return (el.expression || '').substring(0, 22) || 'Formül';
  if (el.type === 'image')      return 'Resim';
  if (el.type === 'line')       return 'Çizgi';
  if (el.type === 'rect')       return 'Kutu';
  if (el.type === 'barcode')    return `Barkod (${el.field || ''})`;
  return el.type;
}

// ═══ DESIGNER CANVAS ═════════════════════════════════════════════
function DesignerCanvas({ layout, scale, selectedId, onSelect, onUpdateElement, onUpdateBand, snapStep, showGuides, activeGuides, setActiveGuides, onDropField }) {
  const dim = (layout.page_orientation === 'landscape')
    ? { w: PAPER_SIZES[layout.paper_size]?.h || 297, h: PAPER_SIZES[layout.paper_size]?.w || 210 }
    : { w: PAPER_SIZES[layout.paper_size]?.w || 210, h: PAPER_SIZES[layout.paper_size]?.h || 297 };
  const innerLeft = layout.margin_left;
  const innerWidth = dim.w - layout.margin_left - layout.margin_right;

  // Band yığın yüksekliği — band'ları sırayla yerleştirmek için. Page footer hariç.
  const stackOrder = ['report_header', 'page_header', 'group_header', 'master', 'detail', 'group_footer'];
  const bandTops = {};
  let cursor = layout.margin_top;
  for (const b of stackOrder) {
    bandTops[b] = cursor;
    if (layout.bands[b]?.visible) cursor += layout.bands[b].height;
  }
  bandTops.page_footer = dim.h - layout.margin_bottom - (layout.bands.page_footer?.height || 0);
  bandTops.report_footer = bandTops.page_footer - (layout.bands.report_footer?.height || 0);

  const pageH = Math.max(dim.h, cursor + (layout.bands.page_footer?.height || 0) + layout.margin_bottom + 20) * scale;
  const pageW = dim.w * scale;

  // Snap + alignment helper'ları — drag/resize sırasında diğer element'lerin x/y'lerine yakın koordinatlara yapışma
  const SNAP_TOLERANCE = 1.2; // mm
  const snapTo = (val, step) => step > 0 ? Math.round(val / step) * step : val;

  const computeSnap = (el, draftX, draftY, draftW, draftH) => {
    let x = snapTo(draftX, snapStep);
    let y = snapTo(draftY, snapStep);
    let guideX = null, guideY = null;
    if (showGuides) {
      // Diğer element'lerin (aynı band içindeki) kenarlarına göre yakınlık
      const targets = layout.elements.filter(e => e.id !== el.id && e.band === el.band);
      const candidatesX = [0, innerWidth]; // page edges
      const candidatesY = [0, layout.bands[el.band]?.height || 0];
      for (const t of targets) {
        candidatesX.push(t.x || 0, (t.x || 0) + (t.w || 0));
        candidatesY.push(t.y || 0, (t.y || 0) + (t.h || 0));
      }
      for (const c of candidatesX) {
        if (Math.abs(c - x) < SNAP_TOLERANCE) { x = c; guideX = c; break; }
        if (Math.abs(c - (x + draftW)) < SNAP_TOLERANCE) { x = c - draftW; guideX = c; break; }
      }
      for (const c of candidatesY) {
        if (Math.abs(c - y) < SNAP_TOLERANCE) { y = c; guideY = c; break; }
        if (Math.abs(c - (y + draftH)) < SNAP_TOLERANCE) { y = c - draftH; guideY = c; break; }
      }
    }
    return { x: Math.max(0, x), y: Math.max(0, y), guideX, guideY };
  };

  // ─── Drag-from-tree: drop hedefi ───────────────────────────────
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; };
  const handleDrop = (e) => {
    e.preventDefault();
    let payload = null;
    try { payload = JSON.parse(e.dataTransfer.getData('application/json') || '{}'); } catch (_) {}
    if (!payload?.fieldKey) return;
    // Drop konumunu mm cinsinden bul ve hangi band'a düştüğünü tespit et
    const rect = e.currentTarget.getBoundingClientRect();
    const xMm = (e.clientX - rect.left) / scale;
    const yMm = (e.clientY - rect.top)  / scale;
    let targetBand = 'master';
    for (const b of BANDS) {
      const cfg = layout.bands[b];
      if (!cfg?.visible) continue;
      const top = bandTops[b];
      if (yMm >= top && yMm <= top + cfg.height) { targetBand = b; break; }
    }
    const localX = Math.max(0, xMm - innerLeft);
    const localY = Math.max(0, yMm - (bandTops[targetBand] || 0));
    onDropField({ ...payload, band: targetBand }, localX, localY);
  };

  return (
    <div onDragOver={handleDragOver} onDrop={handleDrop}
      style={{ background: '#fff', boxShadow: '0 4px 18px rgba(0,0,0,0.18)', position: 'relative', width: pageW, height: pageH, flexShrink: 0 }}
      onClick={() => onSelect(null)}>
      {/* Kenar boşluğu kılavuzu */}
      <div style={{ position: 'absolute', inset: 0, border: '1px dashed #cbd5e1',
        margin: `${layout.margin_top * scale}px ${layout.margin_right * scale}px ${layout.margin_bottom * scale}px ${layout.margin_left * scale}px`, pointerEvents: 'none' }}/>
      {/* Band'lar */}
      {BANDS.map(b => {
        const cfg = layout.bands[b];
        if (!cfg?.visible) return null;
        const top = (bandTops[b] || 0) * scale;
        const h = cfg.height * scale;
        return (
          <React.Fragment key={b}>
            <div style={{
              position: 'absolute', left: innerLeft * scale, top, width: innerWidth * scale, height: h,
              background: BAND_COLORS[b], border: '1px dashed rgba(0,0,0,0.18)', pointerEvents: 'none',
            }}>
              <div style={{ position: 'absolute', top: 2, left: 4, fontSize: 9, color: '#475569', letterSpacing: 0.5, fontWeight: 700, textTransform: 'uppercase' }}>{BAND_LABELS[b]}</div>
              <div style={{ position: 'absolute', top: 2, right: 4, fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>{cfg.height.toFixed(1)}mm</div>
            </div>
            {/* Band yüksekliği yeniden boyutlandırma tutamağı — alt kenardan sürükle */}
            <div
              title={`${BAND_LABELS[b]} yüksekliğini sürükle`}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const startY = e.clientY;
                const startH = cfg.height;
                const move = (ev) => {
                  const deltaMm = (ev.clientY - startY) / scale;
                  const newH = Math.max(0, snapTo(startH + deltaMm, snapStep));
                  onUpdateBand(b, { height: newH });
                };
                const up = () => {
                  window.removeEventListener('mousemove', move);
                  window.removeEventListener('mouseup', up);
                };
                window.addEventListener('mousemove', move);
                window.addEventListener('mouseup', up);
              }}
              style={{
                position: 'absolute', left: innerLeft * scale, top: top + h - 3,
                width: innerWidth * scale, height: 6, cursor: 'ns-resize', zIndex: 4,
                background: 'transparent',
              }}
            >
              <div style={{ position: 'absolute', left: '50%', top: 1, transform: 'translateX(-50%)',
                width: 40, height: 4, background: '#3b82f6', borderRadius: 2, opacity: 0.5 }}/>
            </div>
          </React.Fragment>
        );
      })}
      {/* Alignment guides — sürükleme sırasında mavi çizgi */}
      {activeGuides.x != null && (
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: (innerLeft + activeGuides.x) * scale,
          width: 1, background: '#3b82f6', pointerEvents: 'none', zIndex: 5 }}/>
      )}
      {activeGuides.y != null && (
        <div style={{ position: 'absolute', left: 0, right: 0, top: ((bandTops[activeGuides.band] || 0) + activeGuides.y) * scale,
          height: 1, background: '#3b82f6', pointerEvents: 'none', zIndex: 5 }}/>
      )}
      {/* Elementler */}
      {layout.elements.map(el => {
        const cfg = layout.bands[el.band];
        if (!cfg?.visible) return null;
        const bandY = bandTops[el.band] || 0;
        const x = (innerLeft + (el.x || 0)) * scale;
        const y = (bandY + (el.y || 0)) * scale;
        return (
          <Rnd
            key={el.id}
            size={{ width: (el.w || 30) * scale, height: (el.h || 8) * scale }}
            position={{ x, y }}
            onDrag={(e, d) => {
              const dx = (d.x / scale) - innerLeft;
              const dy = (d.y / scale) - bandY;
              const r = computeSnap(el, dx, dy, el.w || 0, el.h || 0);
              setActiveGuides({ x: r.guideX, y: r.guideY, band: el.band });
            }}
            onDragStop={(e, d) => {
              const dx = (d.x / scale) - innerLeft;
              const dy = (d.y / scale) - bandY;
              const r = computeSnap(el, dx, dy, el.w || 0, el.h || 0);
              setActiveGuides({ x: null, y: null });
              onUpdateElement(el.id, { x: r.x, y: r.y });
            }}
            onResize={(e, dir, ref, delta, pos) => {
              const w = parseFloat(ref.style.width) / scale;
              const h = parseFloat(ref.style.height) / scale;
              const dx = (pos.x / scale) - innerLeft;
              const dy = (pos.y / scale) - bandY;
              const r = computeSnap(el, dx, dy, w, h);
              setActiveGuides({ x: r.guideX, y: r.guideY, band: el.band });
            }}
            onResizeStop={(e, dir, ref, delta, pos) => {
              const w = parseFloat(ref.style.width) / scale;
              const h = parseFloat(ref.style.height) / scale;
              const dx = (pos.x / scale) - innerLeft;
              const dy = (pos.y / scale) - bandY;
              const r = computeSnap(el, dx, dy, w, h);
              setActiveGuides({ x: null, y: null });
              onUpdateElement(el.id, { w: snapTo(w, snapStep), h: snapTo(h, snapStep), x: r.x, y: r.y });
            }}
            bounds="parent"
            onClick={(e) => { e.stopPropagation(); onSelect(el.id); }}
            style={{
              border: selectedId === el.id ? '2px solid #3b82f6' : '1px solid rgba(0,0,0,0.15)',
              boxSizing: 'border-box', cursor: 'move',
              transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
              transformOrigin: 'center center', zIndex: selectedId === el.id ? 2 : 1,
            }}>
            <ElementInnerPreview el={el} layout={layout}/>
          </Rnd>
        );
      })}
    </div>
  );
}

function ElementInnerPreview({ el, layout }) {
  const sample = SAMPLE_MAIN[0];
  const sampleDetail = SAMPLE_DETAIL_BY_MASTER[sample.id]?.[0];
  const fontSize = Math.max(7, (el.font_size || layout.font_size || 10));
  const baseStyle = {
    width: '100%', height: '100%', overflow: 'hidden',
    fontFamily: layout.font_family, fontSize: fontSize + 'pt',
    fontWeight: el.bold ? 700 : 400, fontStyle: el.italic ? 'italic' : 'normal',
    color: el.color || '#000', textAlign: el.align || 'left',
    display: 'flex', alignItems: el.valign === 'top' ? 'flex-start' : el.valign === 'bottom' ? 'flex-end' : 'center',
    justifyContent: el.align === 'center' ? 'center' : el.align === 'right' ? 'flex-end' : 'flex-start',
    padding: '1px 3px',
    background: el.background || 'transparent',
    border: (el.border_color && el.border_width) ? `${el.border_width}px solid ${el.border_color}` : undefined,
    boxSizing: 'border-box', whiteSpace: 'pre-wrap', lineHeight: 1.2,
  };

  if (el.type === 'text')  return <div style={baseStyle}>{el.text || ''}</div>;
  if (el.type === 'field') {
    const obj = el.data_source === 'detail' ? sampleDetail : sample;
    const v = obj?.[el.field] ?? '';
    return <div style={baseStyle} title={`${el.data_source}.${el.field}`}>{String(v)}</div>;
  }
  if (el.type === 'expression') {
    const r = evaluateExpr(el.expression || '', { current: sample, rows: SAMPLE_MAIN, context: {} });
    return <div style={baseStyle} title={el.expression}>{typeof r.value === 'number' ? r.value.toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : String(r.value ?? '')}</div>;
  }
  if (el.type === 'image') {
    if (el.src) return <img src={el.src} alt="" style={{ width: '100%', height: '100%', objectFit: el.fit || 'contain' }}/>;
    return <div style={{ ...baseStyle, background: 'repeating-linear-gradient(45deg, #f1f5f9 0 4px, transparent 4px 8px)', color: '#94a3b8', justifyContent: 'center' }}>RESİM</div>;
  }
  if (el.type === 'line')    return <div style={{ width: '100%', height: '100%', background: el.color || '#000' }}/>;
  if (el.type === 'rect')    return <div style={{ width: '100%', height: '100%', border: `${el.border_width || 1}px solid ${el.border_color || el.color || '#000'}`, background: el.background || el.fill || 'transparent', boxSizing: 'border-box' }}/>;
  if (el.type === 'barcode') return <div style={{ ...baseStyle, justifyContent: 'center', fontFamily: 'monospace', letterSpacing: 1 }}>‖‖|‖|‖||‖|‖‖||‖|</div>;
  return null;
}

// ═══ ELEMENT PROPS PANEL ═════════════════════════════════════════
function ElementPropsPanel({ el, layout, onChange, onDelete, onDuplicate, onMoveZ }) {
  // Field dropdown'u merkezi registry'den dolsun — kullanıcı anahtar yazmasın
  const fieldOptions = (() => {
    const ds = el.data_source || 'main';
    return getFieldsFor(ds, layout, 'stock_list');
  })();

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: 13 }}>{elementIcon(el.type)} {el.type.toUpperCase()}</strong>
        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{el.band}</span>
      </div>

      <Group title="Konum / Boyut">
        <Row>
          <Field label="X (mm)"><input type="number" step="0.5" value={(el.x ?? 0).toFixed(1)} onChange={e => onChange({ x: parseFloat(e.target.value) || 0 })} style={inputS}/></Field>
          <Field label="Y (mm)"><input type="number" step="0.5" value={(el.y ?? 0).toFixed(1)} onChange={e => onChange({ y: parseFloat(e.target.value) || 0 })} style={inputS}/></Field>
        </Row>
        <Row>
          <Field label="G (mm)"><input type="number" step="0.5" value={(el.w ?? 0).toFixed(1)} onChange={e => onChange({ w: parseFloat(e.target.value) || 0 })} style={inputS}/></Field>
          <Field label="Y (mm)"><input type="number" step="0.5" value={(el.h ?? 0).toFixed(1)} onChange={e => onChange({ h: parseFloat(e.target.value) || 0 })} style={inputS}/></Field>
        </Row>
        <Field label="Döndür (derece)">
          <div style={{ display: 'flex', gap: 4 }}>
            {[0, 90, 180, 270].map(deg => (
              <button key={deg} onClick={() => onChange({ rotation: deg })}
                style={{ flex: 1, padding: '4px 0', border: '1px solid var(--border-secondary)', borderRadius: 4,
                         background: (el.rotation || 0) === deg ? 'var(--accent-primary)' : 'transparent',
                         color: (el.rotation || 0) === deg ? '#fff' : 'inherit', cursor: 'pointer', fontSize: 11 }}>{deg}°</button>
            ))}
            <input type="number" value={el.rotation || 0} onChange={e => onChange({ rotation: parseFloat(e.target.value) || 0 })} style={{ ...inputS, width: 60 }}/>
          </div>
        </Field>
      </Group>

      {/* İçerik */}
      {el.type === 'text' && (
        <Group title="Metin">
          <textarea value={el.text || ''} onChange={e => onChange({ text: e.target.value })} style={{ ...inputS, minHeight: 60 }}/>
        </Group>
      )}
      {(el.type === 'field' || el.type === 'barcode') && (
        <Group title={el.type === 'barcode' ? 'Barkod Kaynağı' : 'Alan Kaynağı'}>
          <Field label="Veri Kümesi">
            <select value={el.data_source || 'main'} onChange={e => onChange({ data_source: e.target.value, field: '' })} style={inputS}>
              <option value="main">Ana (Master)</option><option value="detail">Detay</option><option value="context">Bağlam (başlık vs.)</option>
            </select>
          </Field>
          <Field label="Alan">
            <select value={el.field || ''} onChange={e => onChange({ field: e.target.value })} style={inputS}>
              <option value="">— seç —</option>
              {fieldOptions.map(f => <option key={f.key} value={f.key}>{f.label} [{f.key}]</option>)}
            </select>
          </Field>
          {el.type === 'barcode' && (
            <>
              <Field label="Barkod Türü">
                <select value={el.format || 'CODE128'} onChange={e => onChange({ format: e.target.value })} style={inputS}>
                  <option value="CODE128">CODE128</option>
                  <option value="EAN13">EAN-13</option>
                  <option value="EAN8">EAN-8</option>
                  <option value="UPC">UPC-A</option>
                  <option value="CODE39">CODE39</option>
                  <option value="ITF14">ITF-14</option>
                  <option value="MSI">MSI</option>
                  <option value="pharmacode">Pharmacode</option>
                </select>
              </Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={el.show_text !== false} onChange={e => onChange({ show_text: e.target.checked })}/>
                Barkod altında yazıyı göster
              </label>
            </>
          )}
        </Group>
      )}
      {el.type === 'expression' && (
        <Group title="Formül (Expression)">
          <Field label="Veri Kümesi (Sum/Count kapsamı)">
            <select value={el.data_source || 'main'} onChange={e => onChange({ data_source: e.target.value })} style={inputS}>
              <option value="main">Ana (Master)</option><option value="detail">Detay</option>
            </select>
          </Field>
          <Field label={
            <span>İfade <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>· Sum([qty]) / Count() / Avg([f]) / IIF([qty]&lt;0,&quot;-&quot;,&quot;+&quot;)</span></span>
          }>
            <textarea value={el.expression || ''} onChange={e => onChange({ expression: e.target.value })} style={{ ...inputS, minHeight: 50, fontFamily: 'monospace', fontSize: 11 }}/>
          </Field>
          <Field label="Format">
            <select value={el.format || ''} onChange={e => onChange({ format: e.target.value })} style={inputS}>
              <option value="">Otomatik</option><option value="integer">Tam sayı</option>
              <option value="currency">Para birimi (TR)</option><option value="percent">Yüzde</option>
            </select>
          </Field>
        </Group>
      )}
      {el.type === 'image' && (
        <Group title="Görsel">
          <Field label="URL"><input type="text" value={el.src || ''} onChange={e => onChange({ src: e.target.value })} style={inputS}/></Field>
          <Field label="Sığdırma">
            <select value={el.fit || 'contain'} onChange={e => onChange({ fit: e.target.value })} style={inputS}>
              <option value="contain">İçeri</option><option value="cover">Kapla</option><option value="fill">Doldur</option>
            </select>
          </Field>
        </Group>
      )}

      {/* Yazı stili */}
      {(el.type === 'text' || el.type === 'field' || el.type === 'expression' || el.type === 'barcode') && (
        <Group title="Yazı / Hizalama">
          <Field label="Yazı Tipi">
            <select value={el.font_family || ''} onChange={e => onChange({ font_family: e.target.value })} style={inputS}>
              <option value="">Varsayılan</option>
              {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </Field>
          <Row>
            <Field label="Punto"><input type="number" min="6" max="60" value={el.font_size || 10} onChange={e => onChange({ font_size: parseFloat(e.target.value) || 10 })} style={inputS}/></Field>
            <Field label="Renk"><input type="color" value={el.color || '#000000'} onChange={e => onChange({ color: e.target.value })} style={{ width: '100%', height: 28 }}/></Field>
          </Row>
          <Row>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="checkbox" checked={!!el.bold} onChange={e => onChange({ bold: e.target.checked })}/> Kalın</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="checkbox" checked={!!el.italic} onChange={e => onChange({ italic: e.target.checked })}/> İtalik</label>
          </Row>
          <Row>
            <Field label="Yatay Hiza">
              <select value={el.align || 'left'} onChange={e => onChange({ align: e.target.value })} style={inputS}>
                <option value="left">Sol</option><option value="center">Orta</option><option value="right">Sağ</option>
              </select>
            </Field>
            <Field label="Dikey Hiza">
              <select value={el.valign || 'middle'} onChange={e => onChange({ valign: e.target.value })} style={inputS}>
                <option value="top">Üst</option><option value="middle">Orta</option><option value="bottom">Alt</option>
              </select>
            </Field>
          </Row>
        </Group>
      )}

      {/* Kutu/Çizgi/Arkaplan */}
      {(el.type === 'rect' || el.type === 'line' || el.type === 'text' || el.type === 'field' || el.type === 'expression') && (
        <Group title={el.type === 'rect' ? 'Çerçeve & Dolgu' : 'Arkaplan & Çerçeve'}>
          <Row>
            <Field label="Çerçeve Renk"><input type="color" value={el.border_color || el.color || '#000000'} onChange={e => onChange({ border_color: e.target.value })} style={{ width: '100%', height: 28 }}/></Field>
            <Field label="Kalınlık"><input type="number" step="0.5" value={el.border_width ?? (el.type === 'line' ? (el.thickness || 1) : 0)} onChange={e => onChange({ border_width: parseFloat(e.target.value) || 0, thickness: parseFloat(e.target.value) || 0 })} style={inputS}/></Field>
          </Row>
          {el.type !== 'line' && (() => {
            const isTransparent = !el.background || el.background === 'transparent' || el.background === '';
            const validHex = /^#[0-9a-fA-F]{6}$/.test(el.background || '');
            const colorVal = validHex ? el.background : '#ffffff';
            return (
              <Row>
                <Field label="Arkaplan Tipi">
                  <select value={isTransparent ? 'transparent' : 'color'}
                    onChange={e => onChange({ background: e.target.value === 'transparent' ? 'transparent' : colorVal })}
                    style={inputS}>
                    <option value="transparent">Şeffaf</option>
                    <option value="color">Renkli</option>
                  </select>
                </Field>
                {!isTransparent && (
                  <Field label="Renk">
                    <input type="color" value={colorVal}
                      onChange={e => onChange({ background: e.target.value })}
                      style={{ width: '100%', height: 28 }}/>
                  </Field>
                )}
              </Row>
            );
          })()}
        </Group>
      )}

      {/* Davranış */}
      {(el.type === 'field' || el.type === 'expression') && (
        <Group title="Davranış">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={!!el.hide_if_zero} onChange={e => onChange({ hide_if_zero: e.target.checked })}/>
            Değer 0 / boş ise gizle
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={!!el.page_break_before} onChange={e => onChange({ page_break_before: e.target.checked })}/>
            Önce sayfa atla
          </label>
        </Group>
      )}

      {/* Conditional formatting */}
      {(el.type === 'field' || el.type === 'expression') && (
        <Group title="Koşullu Biçimlendirme">
          <ConditionalEditor list={el.conditional || []} onChange={(c) => onChange({ conditional: c })}/>
        </Group>
      )}

      {/* Z-order — üst üste binen elementlerin sıralaması (array'de sondaki en üstte render edilir) */}
      <Group title="Sıralama (Z-Order)">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          <Button size="sm" variant="ghost" onClick={() => onMoveZ && onMoveZ('front')} title="En öne getir">⏫ En Öne</Button>
          <Button size="sm" variant="ghost" onClick={() => onMoveZ && onMoveZ('forward')} title="Bir adım öne">🔼 Öne</Button>
          <Button size="sm" variant="ghost" onClick={() => onMoveZ && onMoveZ('back')} title="En arkaya gönder">⏬ En Arkaya</Button>
          <Button size="sm" variant="ghost" onClick={() => onMoveZ && onMoveZ('backward')} title="Bir adım arkaya">🔽 Arkaya</Button>
        </div>
      </Group>

      <div style={{ display: 'flex', gap: 4 }}>
        <Button size="sm" variant="ghost" onClick={onDuplicate}>📋 Çoğalt</Button>
        <Button size="sm" variant="ghost" onClick={onDelete} style={{ color: 'var(--danger)', flex: 1 }}>🗑 Sil</Button>
      </div>
    </div>
  );
}

function ConditionalEditor({ list, onChange }) {
  const set = (i, patch) => onChange(list.map((c, j) => j === i ? { ...c, ...patch } : c));
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const add = () => onChange([...list, { when_expr: '[value] < 0', color: '#dc2626', bold: true }]);
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {list.map((c, i) => (
        <div key={i} style={{ padding: 6, background: 'var(--bg-secondary)', borderRadius: 4, display: 'grid', gap: 4 }}>
          <Field label="Koşul (true → uygula)">
            <input value={c.when_expr || ''} onChange={e => set(i, { when_expr: e.target.value })} style={{ ...inputS, fontFamily: 'monospace' }} placeholder="[total_qty] < 10"/>
          </Field>
          <Row>
            <Field label="Renk"><input type="color" value={c.color || '#000000'} onChange={e => set(i, { color: e.target.value })} style={{ width: '100%', height: 24 }}/></Field>
            <Field label="Arka Plan"><input type="color" value={c.background || '#ffffff'} onChange={e => set(i, { background: e.target.value })} style={{ width: '100%', height: 24 }}/></Field>
          </Row>
          <Row>
            <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}><input type="checkbox" checked={!!c.bold} onChange={e => set(i, { bold: e.target.checked })}/> Kalın</label>
            <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}><input type="checkbox" checked={!!c.italic} onChange={e => set(i, { italic: e.target.checked })}/> İtalik</label>
            <button onClick={() => remove(i)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--danger)' }}>×</button>
          </Row>
        </div>
      ))}
      <Button size="sm" variant="ghost" onClick={add}>+ Koşul Ekle</Button>
    </div>
  );
}

// ═══ Mini stil yardımcıları ══════════════════════════════════════
const inputS = { width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border-secondary)', background: 'var(--bg-input, var(--bg-secondary))', color: 'var(--text-primary)', fontSize: 12 };
const collapseBtn = { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)' };
function Group({ title, children }) {
  return <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{title}</div>
    <div style={{ display: 'grid', gap: 6 }}>{children}</div>
  </div>;
}
function Field({ label, children }) {
  return <label style={{ display: 'grid', gap: 2 }}>
    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{label}</span>
    {children}
  </label>;
}
function Row({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>{children}</div>;
}

// Eski default export olan renderPrintHtml dış kullanım için — engine'den re-export
export { renderPrintHtml };
